import React, { useEffect, useRef, useState } from 'react';
import { Modal, Box, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel, Autocomplete, Grid, ListItemText, IconButton } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';

// A Service Provider event: like a session, but with no customer, so no session row or task is made.
// People are scanned in at it on mobile. At least one employee is required.
function ScheduleEventModal(props) {
  const [scheduleName, setScheduleName] = useState('');
  const [resource, setResource] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());
  const [notes, setNotes] = useState('');
  const [taskType, setTaskType] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const [errors, setErrors] = useState({
    scheduleName: false,
    employees: false,
    selectedDate: false,
    startTime: false,
    endTime: false,
    taskType: false,
  });

  const validate = () => {
    const newErrors = {
      scheduleName: !scheduleName,
      employees: employees.length === 0,
      selectedDate: !selectedDate,
      startTime: !startTime || moment(startTime).isSameOrAfter(endTime),
      endTime: !endTime || moment(endTime).isSameOrBefore(startTime),
      taskType: !taskType,
    };
    setErrors(newErrors);
    setSubmitted(true);
    return Object.values(newErrors).every(error => !error);
  };

  const handleEmployeesChange = (event, newValue) => {
    if (props.user.permissions.ScheduleSessionForOtherEmployees) {
      setEmployees(newValue);
    } else {
      setEmployees(newValue ? [newValue] : []);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // A ref blocks rapid submissions even before React renders the disabled button.
    if (isSubmittingRef.current) {
      return;
    }

    if (!validate()) {
      return;
    }

    let date = moment(selectedDate);
    let schedule = {
      schedule_name: scheduleName,
      resource_id: resource?.resource_id ?? null,
      employee_ids: employees.map(emp => emp.id),
      date: date.format('YYYY-MM-DD'),
      start_time: moment(startTime).set({ year: date.year(), month: date.month(), date: date.date() }).utc().format('YYYY-MM-DDTHH:mm:00'),
      end_time: moment(endTime).set({ year: date.year(), month: date.month(), date: date.date() }).utc().format('YYYY-MM-DDTHH:mm:00'),
      max_capacity: null,
      task_type: taskType,
      notes: notes ? notes : null,
      company_id: props.comp_id,
      created_by: props.user.emp_id,
      is_event: true,
    };

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await axios.post(APIURL + '/master_schedule', schedule);
      props.reloadScheduleData();
      props.onClose();
      props.onSuccess("Event scheduled successfully");
    } catch (error) {
      if (error.response?.status === 400) {
        props.onError(error.response.data.message);
      } else {
        props.onError("An error occurred while scheduling event");
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmittingRef.current) {
      props.onClose();
    }
  };

  useEffect(() => {
    if (!props.open) {
      setScheduleName('');
      setResource(null);
      setEmployees([]);
      setSelectedDate(new Date());
      setStartTime(new Date());
      setEndTime(new Date());
      setNotes('');
      setTaskType('');
      setSubmitted(false);
      setErrors({
        scheduleName: false,
        employees: false,
        selectedDate: false,
        startTime: false,
        endTime: false,
        taskType: false,
      });
    } else {
      const defaultEmployee = props.employees?.find((employee) => employee.id === props.user.emp_id);
      setEmployees(defaultEmployee ? [defaultEmployee] : []);
    }
  }, [props.open]);

  return (
    <Modal enforceFocus={false}
      open={props.open}
      onClose={handleClose}
      aria-labelledby="schedule-event-modal-title"
      sx={{ display: 'flex', justifyContent: 'center', overflowY: 'auto', p: 2 }}
    >
      <Box sx={{ width: 500, maxWidth: '100%', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, height: 'fit-content' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography id="schedule-event-modal-title" variant="h6" component="h2">
            Schedule New Event
          </Typography>
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.25 }}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            label="Event Name"
            fullWidth
            margin="normal"
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
            error={errors.scheduleName && submitted}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
          <FormControl fullWidth margin="normal">
            <Autocomplete
              id="event-location-select"
              options={props.resources}
              value={resource}
              getOptionLabel={(option) => option.resource_name || ''}
              isOptionEqualToValue={(option, value) => option.resource_id === value.resource_id}
              onChange={(event, newValue) => setResource(newValue)}
              renderOption={(props, option) => (
                <li {...props} key={option.resource_id}>
                  {option.resource_name}
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Location (optional)" />}
            />
          </FormControl>
          <FormControl fullWidth margin="normal">
            <Autocomplete
              multiple={props.user.permissions.ScheduleSessionForOtherEmployees}
              id="event-employee-select"
              options={props.employees}
              getOptionLabel={(option) => option.employee_name}
              renderInput={(params) => <TextField {...params} label={props.user.permissions.ScheduleSessionForOtherEmployees ? "Employees" : "Employee"} error={errors.employees && submitted} />}
              onChange={handleEmployeesChange}
              value={props.user.permissions.ScheduleSessionForOtherEmployees ? employees : employees[0] || null}
              disabled={!props.user.permissions.ScheduleSessionForOtherEmployees && employees.length >= 1}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    <ListItemText primary={option.employee_name} />
                  </li>
                )
              }}
            />
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="event-task-type-select-label">Task Type</InputLabel>
                    <Select
                      labelId="event-task-type-select-label"
                      label="Task Type"
                      value={taskType}
                      onChange={(e) => setTaskType(e.target.value)}
                      error={errors.taskType && submitted}
                    >
                      {props.taskTypes.map((taskType, index) => (
                        <MenuItem key={index} value={taskType}>
                          {taskType}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={6}>
                  <DatePicker
                    label="Select Date"
                    value={moment(selectedDate).isValid() ? new Date(selectedDate) : null}
                    onChange={setSelectedDate}
                    slotProps={{ textField: { fullWidth: true, error: errors.selectedDate && submitted } }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Grid container spacing={2} alignItems="center">
              <Grid size={6}>
                <TimePicker
                  label="Start Time"
                  value={startTime}
                  onChange={setStartTime}
                  slotProps={{ textField: { fullWidth: true, error: errors.startTime && submitted } }}
                />
              </Grid>
              <Grid size={6}>
                <TimePicker
                  label="End Time"
                  value={endTime}
                  onChange={setEndTime}
                  slotProps={{ textField: { fullWidth: true, error: errors.endTime && submitted } }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
          <TextField
            margin="normal"
            fullWidth
            label="Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
          <Button type="submit" fullWidth variant="contained" disabled={isSubmitting} sx={{ mt: 3, mb: 2 }}>
            {isSubmitting ? 'Scheduling…' : 'Schedule Event'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default ScheduleEventModal;
